/**
 * OwnersOfGoalsStore — the Owners of Goals feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY owners-of-goals module that talks to the
 * API, and it does so exclusively through the {@link EngineeringVectorApi}
 * facade (`api.documents<OwnersOfGoals>('ownersOfGoals')` + `api.identityProvider`).
 * It never imports ApiClient or calls fetch. The React hook calls this store;
 * pages call the hook.
 *
 * Persistence model: a SINGLE canonical document with the fixed id
 * `owners_of_goals` in the 'ownersOfGoals' generic-store collection (DDB pointer
 * row + S3 body). The full entries table lives in the S3 body; only lightweight
 * discovery attributes (updatedBy/lastUpdated/entryCount) are mirrored onto the
 * pointer row. Tenant scoping is automatic — DocumentStore routes by the active
 * team bundle.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { DocumentStore, EngineeringVectorApi } from '../../api';
import type { OwnerEntry, OwnersOfGoals } from './types';
import {
  OWNERS_OF_GOALS_COLLECTION,
  OWNERS_OF_GOALS_ID,
  defaultOwnersRecord,
  normalizeOwnerEntries,
  ownersOfGoalsIndexAttributes,
} from './ownersOfGoalsModel';

export class OwnersOfGoalsStore {
  private readonly docs: DocumentStore<OwnersOfGoals>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<OwnersOfGoals>(OWNERS_OF_GOALS_COLLECTION);
  }

  /**
   * Loads the canonical record, SEEDING it with the default owners the first
   * time it does not exist (persisted so it is stable across reloads). Always
   * returns a record.
   */
  async load(): Promise<OwnersOfGoals> {
    const existing = await this.docs.get(OWNERS_OF_GOALS_ID);
    if (existing) return existing;
    const identity = await this.api.identityProvider.getIdentity();
    const seeded = defaultOwnersRecord(identity.alias);
    await this.docs.save(
      seeded.id,
      seeded,
      ownersOfGoalsIndexAttributes(seeded),
    );
    return seeded;
  }

  /**
   * Saves the owner rows (normalized/trimmed), stamping updatedBy (from
   * identity) and lastUpdated. Uses save-or-update so it works whether or not
   * the record already exists. Returns the saved record.
   */
  async save(entries: OwnerEntry[]): Promise<OwnersOfGoals> {
    const identity = await this.api.identityProvider.getIdentity();
    const record: OwnersOfGoals = {
      id: OWNERS_OF_GOALS_ID,
      entries: normalizeOwnerEntries(entries),
      lastUpdated: new Date().toISOString(),
      updatedBy: identity.alias,
    };
    const existing = await this.docs.get(OWNERS_OF_GOALS_ID);
    const attrs = ownersOfGoalsIndexAttributes(record);
    if (existing) {
      await this.docs.update(record.id, record, attrs);
    } else {
      await this.docs.save(record.id, record, attrs);
    }
    return record;
  }
}
